import moment from 'moment'
import lang from '@/lang'
import config from '@/config'
import { ListDefaultSettings } from '@/modules/base/components/list/constants'
import { dateFieldComparator, dateFieldFilterComparator } from '@/modules/base/components/list/comparator'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'

import { LIST_TYPES } from '@/redux/ducks/lists/index'
import { transactionTypes } from '@/modules/base/util/constants'

const urlPrefix = `${config.resourceHost}banking`
const listConfig = {
  getColumns: type => ([
    {
      field: 'bookingDate',
      headerName: lang.generics.date,
      sort: false,
      suppressMovable: true,
      width: 100,
      filter: 'agDateColumnFilter',
      cellRenderer: 'dateCellRenderer',
      tooltipValueGetter: ({ value }) => value ? moment(value).format(config.dateFormat.client) : '',
      comparator: dateFieldComparator,
      filterParams: {
        comparator: dateFieldFilterComparator,
        filterOptions: ListDefaultSettings.DATE_FILTER_OPTIONS,
        inRangeInclusive: true,
      },
      getQuickFilterText: ({ value }) => value ? moment(value).format('DD.MM.YYYY YYYY-MM-DD') : '',
    },
    {
      field: 'bankAccountName',
      headerName: lang.bookingSelectionList.columns.bankAccount,
      filter: 'agSetColumnFilter',
      sort: false,
      width: 100,
      tooltipField: 'bankAccountName',
    },
    {
      field: 'bookingContactName',
      headerName: type === LIST_TYPES.BOOKING_LIST_TYPE
        ? lang.bookingSelectionList.columns.bookingContactName
        : lang.bookingSelectionList.columns.senderContactName,
      sort: 'desc',
      width: 100,
      tooltipField: 'bookingContactName',
    },
    {
      field: 'purposeDescription',
      headerName: lang.bookingAssignment.columns.purposeDescription,
      sort: false,
      width: 100,
      tooltipField: 'purposeDescription',
    },
    {
      field: 'bookingAmount',
      headerName: lang.bookingAssignment.columns.bookingAmount,
      sort: false,
      width: 100,
      cellRenderer: 'amountCellRenderer',
      tooltipValueGetter: ({ value }) => value === null || value === undefined ? '' : formatCurrency(value),
      filter: 'agNumberColumnFilter',
      filterParams: {
        inRangeInclusive: true,
      },
      getQuickFilterText: ({ value }) => {
        const amount = Number(value)
        const localizedAmount = Number.isFinite(amount)
          ? amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : ''

        return `${value || ''} ${localizedAmount}`
      },
    },
  ].map((item) => ({
    ...item,
    suppressMovable: true,
    menuTabs: ['filterMenuTab'],
    headerTooltip: item.headerName,
  }))),
  getFilterItems: (type, bookingData, items = [], transactionType) => {
    const { outstandingAmount: outstandingAmountBookingData, payee, sender, date, supplier, customerData } = bookingData
    const outstandingAmount = transactionType === transactionTypes.TRANSACTION_TYPE_CREDIT_NOTE ? outstandingAmountBookingData * -1 : outstandingAmountBookingData
    let names = []
    const tenPercentOfAmount = outstandingAmount * 0.1

    let format = ''
    if (transactionType === 'invoice') {
      format = config.dateFormat.client
    }
    const dateFrom = date ? moment(date, format).subtract(3, 'days').toISOString() : null
    const dateTo = date ? moment(date, format).add(3, 'days').toISOString() : null

    if (supplier && supplier.aliases) {
      names = [...supplier.aliases]
    }

    if (payee) {
      names.push(payee)
    }

    if (sender) {
      names.push(sender)
    }

    if (customerData?.name) {
      names.push(customerData.name)
    }

    const checkMatch = (name, data) => {
      const { bookingAmount, bookingDate, bookingContactName = '' } = data
      if (name === 'matching') {
        return bookingAmount === outstandingAmount * (bookingAmount < 0 ? -1 : 1)
      } else if (name === 'amount') {
        return bookingAmount < 0
          ? bookingAmount <= (outstandingAmount - tenPercentOfAmount) * -1 && bookingAmount >= (outstandingAmount + tenPercentOfAmount) * -1
          : bookingAmount >= (outstandingAmount - tenPercentOfAmount) && bookingAmount <= (outstandingAmount + tenPercentOfAmount)
      } else if (name === 'payee' || name === 'sender') {
        if (names.length === 0) {
          return false
        } else {
          return !!names.find(name => (bookingContactName || '').toLowerCase().indexOf(name.toLowerCase()) > -1)
        }
      } else if (name === 'date') {
        return moment(bookingDate).isBetween(moment(dateFrom).subtract(1, 'days'), moment(dateTo).subtract(1, 'days'))
      } else {
        return true
      }
    }

    const getCount = (name) => {
      return name === 'all' ? items.length : items.filter(item => checkMatch(name, item)).length
    }

    const check = (name, data) => {
      return checkMatch(name, data)
    }

    return [
      {
        name: 'all',
        title: lang.bookingSelectionList.quickFilter.all,
        priority: 5,
        count: getCount('all'),
      },
      {
        name: 'matching',
        title: lang.bookingSelectionList.quickFilter.matching,
        priority: 1,
        count: getCount('matching'),
        matchItem: check,
      },
      {
        name: 'amount',
        title: lang.bookingSelectionList.quickFilter.amount,
        priority: 2,
        count: getCount('amount'),
        matchItem: check,
      },
      {
        name: type === LIST_TYPES.BOOKING_LIST_TYPE
          ? 'payee'
          : 'sender',
        title: type === LIST_TYPES.BOOKING_LIST_TYPE
          ? lang.bookingSelectionList.quickFilter.payee
          : lang.bookingSelectionList.quickFilter.sender,
        priority: 3,
        count: getCount(type === LIST_TYPES.BOOKING_LIST_TYPE ? 'payee' : 'sender'),
        matchItem: check,
      },
      {
        name: 'date',
        title: lang.bookingSelectionList.quickFilter.date,
        priority: 4,
        count: getCount('date'),
        matchItem: check,
      },
    ]
  },
  urls: {
    resourceUrl: urlPrefix,
    fetchTurnoverListForBankAccount: (options) => {
      const { bookingType = '', bookingStates = [] } = options
      const offsetString = 'offset=0'
      const limitString = `&limit=${99999}` // fix limit
      const bookingTypeString = `&filter=${bookingType}`
      const assignmentStatesString = `&assignmentStates=${encodeURIComponent(JSON.stringify(bookingStates))}`
      return `${urlPrefix}/bankaccount/all/turnover?${offsetString}${limitString}${bookingTypeString}${assignmentStatesString}`
    },
  },
}

export default listConfig
